/**
 * WebSocket Client for Real-Time Communication
 * 
 * Connects to API server WebSocket for:
 * - Sending heartbeats with outlet status
 * - Receiving branding/config updates
 * - Real-time status monitoring
 */

import { io, Socket } from 'socket.io-client';
import { getSyncQueueSize } from '../db/sync-queue';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../config/terminal-urls';
import type { BrandingConfig } from '@photobooth/types';

export interface OutletStatus {
  outletId: string;
  status: 'online' | 'offline' | 'degraded';
  terminals: {
    order: TerminalStatus;
    capture: TerminalStatus;
    edit: TerminalStatus;
  };
  lastHeartbeat: Date;
  activeTransactions: number;
  queuedPrintJobs: number;
}

export interface TerminalStatus {
  connected: boolean;
  lastActivity: Date;
  currentSession?: string;
}

type BrandingUpdateListener = (branding: BrandingConfig) => void;
type ConfigUpdateListener = (config: any) => void;

class WebSocketClient {
  private socket: Socket | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private outletId: string | null = null;
  private terminalRole: 'order' | 'capture' | 'edit' | null = null;
  private brandingListeners: Set<BrandingUpdateListener> = new Set();
  private configListeners: Set<ConfigUpdateListener> = new Set();

  /**
   * Connect to WebSocket server
   */
  async connect(token: string, outletId: string, terminalRole: 'order' | 'capture' | 'edit'): Promise<void> {
    this.outletId = outletId;
    this.terminalRole = terminalRole;

    const wsUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL;
    if (!wsUrl) {
      throw new Error('WebSocket URL not configured');
    }

    this.socket = io(wsUrl, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.stopHeartbeat();
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;
    });

    // Listen for branding updates from admin
    this.socket.on('sync:branding', (branding: BrandingConfig) => {
      console.log('Received branding update');
      this.handleBrandingUpdate(branding);
    });

    // Listen for config updates from admin
    this.socket.on('sync:config', (config: any) => {
      console.log('Received config update');
      this.handleConfigUpdate(config);
    });
  }

  /**
   * Start sending heartbeats
   */
  private startHeartbeat(): void {
    // Send heartbeat every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 30000);

    // Send initial heartbeat immediately
    this.sendHeartbeat();
  }

  /**
   * Stop sending heartbeats
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Send heartbeat with outlet status
   */
  private async sendHeartbeat(): Promise<void> {
    if (!this.socket || !this.outletId) return;

    try {
      const status = await this.collectOutletStatus();
      this.socket.emit('outlet:heartbeat', status);
    } catch (error) {
      console.error('Error sending heartbeat:', error);
    }
  }

  /**
   * Collect current outlet status (transactions from API; no IndexedDB)
   */
  private async collectOutletStatus(): Promise<OutletStatus> {
    if (!this.outletId) {
      throw new Error('Outlet ID not set');
    }

    let activeTransactions = 0;
    let queuedPrintJobs = 0;
    try {
      const apiClient = createPhotoBoothAPI({ baseUrl: getEffectiveApiBaseUrl() });
      const token =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('photobooth_device_token') || localStorage.getItem('photobooth_auth_token')
          : null;
      if (token) apiClient.setAuthToken(token);
      const response = await apiClient.transactions.getByOutlet(this.outletId);
      if (response.success && response.data) {
        const transactions = response.data;
        activeTransactions = transactions.filter(
          t => t.captureStatus !== 'completed' || t.editStatus !== 'completed'
        ).length;
        queuedPrintJobs = transactions.filter(t => t.printStatus === 'queued').length;
      }
    } catch {
      // Use 0 if API fails
    }

    // Get sync queue size
    const syncQueueSize = await getSyncQueueSize();

    // Determine terminal statuses
    const terminals = this.getTerminalStatuses();

    // Determine overall status
    const overallStatus = this.determineOverallStatus(terminals);

    return {
      outletId: this.outletId,
      status: overallStatus,
      terminals,
      lastHeartbeat: new Date(),
      activeTransactions,
      queuedPrintJobs: queuedPrintJobs + syncQueueSize,
    };
  }

  /**
   * Get terminal statuses
   */
  private getTerminalStatuses(): OutletStatus['terminals'] {
    const now = new Date();
    
    // For simplicity, we mark the current terminal as connected
    // In a real implementation, you'd track each terminal separately
    const currentTerminalStatus: TerminalStatus = {
      connected: true,
      lastActivity: now,
    };

    const disconnectedStatus: TerminalStatus = {
      connected: false,
      lastActivity: now,
    };

    return {
      order: this.terminalRole === 'order' ? currentTerminalStatus : disconnectedStatus,
      capture: this.terminalRole === 'capture' ? currentTerminalStatus : disconnectedStatus,
      edit: this.terminalRole === 'edit' ? currentTerminalStatus : disconnectedStatus,
    };
  }

  /**
   * Determine overall outlet status
   */
  private determineOverallStatus(terminals: OutletStatus['terminals']): OutletStatus['status'] {
    const allConnected = terminals.order.connected && 
                         terminals.capture.connected && 
                         terminals.edit.connected;
    const anyConnected = terminals.order.connected || 
                         terminals.capture.connected || 
                         terminals.edit.connected;

    if (allConnected) return 'online';
    if (anyConnected) return 'degraded';
    return 'offline';
  }

  /**
   * Handle branding update from server
   */
  private handleBrandingUpdate(branding: BrandingConfig): void {
    // Store in local storage for persistence
    try {
      localStorage.setItem('branding_config', JSON.stringify(branding));
    } catch (error) {
      console.error('Error storing branding config:', error);
    }

    // Notify listeners
    this.brandingListeners.forEach(listener => {
      try {
        listener(branding);
      } catch (error) {
        console.error('Error in branding listener:', error);
      }
    });
  }

  /**
   * Handle config update from server
   */
  private handleConfigUpdate(config: any): void {
    // Store in local storage for persistence
    try {
      localStorage.setItem('system_config', JSON.stringify(config));
    } catch (error) {
      console.error('Error storing system config:', error);
    }

    // Notify listeners
    this.configListeners.forEach(listener => {
      try {
        listener(config);
      } catch (error) {
        console.error('Error in config listener:', error);
      }
    });
  }

  /**
   * Send terminal status update
   */
  sendTerminalStatus(status: TerminalStatus): void {
    if (!this.socket) return;
    this.socket.emit('terminal:status', status);
  }

  /**
   * Add branding update listener
   */
  addBrandingListener(listener: BrandingUpdateListener): () => void {
    this.brandingListeners.add(listener);
    return () => this.brandingListeners.delete(listener);
  }

  /**
   * Add config update listener
   */
  addConfigListener(listener: ConfigUpdateListener): () => void {
    this.configListeners.add(listener);
    return () => this.configListeners.delete(listener);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.brandingListeners.clear();
    this.configListeners.clear();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.disconnect();
  }
}

// Singleton instance
let wsClientInstance: WebSocketClient | null = null;

export function initializeWebSocketClient(
  token: string,
  outletId: string,
  terminalRole: 'order' | 'capture' | 'edit'
): Promise<WebSocketClient> {
  if (!wsClientInstance) {
    wsClientInstance = new WebSocketClient();
  }
  return wsClientInstance.connect(token, outletId, terminalRole).then(() => wsClientInstance!);
}

export function getWebSocketClient(): WebSocketClient {
  if (!wsClientInstance) {
    throw new Error('WebSocket client not initialized. Call initializeWebSocketClient first.');
  }
  return wsClientInstance;
}
